/**
 * The electronic receipt — on screen, and on paper.
 *
 * Everything printed here comes from the saved receipt, never from the form
 * that issued it: the number the server minted, the time the server stamped,
 * and the signature the server read from the issuing account. What the desk
 * sees is exactly what the patient takes home.
 *
 * The two phone numbers carry the note the requirement asks for — that they
 * are the patient's own direct lines — so a clerk ringing about a deferred
 * balance never reaches an employer's switchboard by mistake.
 */

import { forwardRef, useEffect, useRef, useState } from 'react';
import { Badge, Button, ErrorState, Field, Icon, Input, Modal, Spinner } from '../../components/common';
import { receiptsApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useNotifications } from '../../context/NotificationContext';
import { DEPARTMENT_META, formatDateTime, formatMoney, printArea } from './labels';

const DEFAULT_TITLE = 'إذن تحصيل أجل';

const STATUS = {
  issued: { label: 'آجل — لم يُحصَّل بعد', className: 'border-amber-500/40 bg-amber-500/15 text-amber-100' },
  collected: { label: 'تم التحصيل', className: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-100' },
  void: { label: 'ملغي', className: 'border-slate-500/40 bg-slate-500/15 text-slate-300' },
};

export const ReceiptSheet = forwardRef(function ReceiptSheet({ receipt }, ref) {
  const { institutionName } = usePermissions();
  const p = receipt.patient || {};
  const issued = new Date(receipt.issuedAt || receipt.createdAt);
  const status = STATUS[receipt.status] || STATUS.issued;

  return (
    <div ref={ref} className="print-sheet" dir="rtl" data-receipt-number={receipt.number}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-300 pb-3">
        <div>
          <h2 className="text-lg font-bold">{institutionName || 'المنشأة الطبية'}</h2>
          <h1 className="text-xl font-bold">{receipt.title || DEFAULT_TITLE}</h1>
        </div>
        <div className="text-left" dir="ltr">
          <p className="font-mono text-base font-bold">{receipt.number}</p>
          <p className="muted text-xs">{issued.toLocaleDateString('ar-EG', { dateStyle: 'full' })}</p>
          <p className="muted text-xs">{issued.toLocaleTimeString('ar-EG', { timeStyle: 'short' })}</p>
        </div>
      </div>

      <table className="mt-3">
        <tbody>
          <tr>
            <th style={{ width: '28%' }}>اسم المريض</th>
            <td>{p.fullName || '—'}</td>
          </tr>
          {p.affiliation && (
            <tr>
              <th>جهة الانتساب</th>
              <td>{p.affiliation}</td>
            </tr>
          )}
          {(p.clinic || p.doctor) && (
            <tr>
              <th>العيادة / الطبيب</th>
              <td>{[p.clinic, p.doctor].filter(Boolean).join(' — ')}</td>
            </tr>
          )}
          {(p.phone1 || p.phone2) && (
            <tr>
              <th>أرقام التواصل</th>
              <td>
                <span dir="ltr">{[p.phone1, p.phone2].filter(Boolean).join('  /  ')}</span>
                <div className="muted text-[11px]">أرقام التواصل المباشرة الخاصة بالمريض نفسه</div>
              </td>
            </tr>
          )}
          {p.nationalId && (
            <tr>
              <th>الرقم القومي</th>
              <td dir="ltr" className="text-right">
                {p.nationalId}
              </td>
            </tr>
          )}
          {p.insuranceCode && (
            <tr>
              <th>كود التأمين</th>
              <td dir="ltr" className="text-right">
                {p.insuranceCode}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <table className="mt-3">
        <thead>
          <tr>
            <th style={{ width: '8%' }}>#</th>
            <th>الخدمة</th>
            <th style={{ width: '26%' }}>المبلغ</th>
          </tr>
        </thead>
        <tbody>
          {(receipt.items || []).map((item, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{item.label}</td>
              <td>{formatMoney(item.amount)}</td>
            </tr>
          ))}
          <tr>
            <th colSpan={2}>الإجمالي</th>
            <th>{formatMoney(receipt.total)}</th>
          </tr>
        </tbody>
      </table>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm">
            <b>الحالة:</b> {status.label}
          </p>
          {receipt.collectedAt && <p className="muted text-xs">تاريخ التحصيل: {formatDateTime(receipt.collectedAt)}</p>}
        </div>
        <div className="rounded-lg border-2 border-dashed border-slate-400 px-4 py-2 text-center">
          <p className="text-[11px] muted">توقيع إلكتروني</p>
          <p className="font-bold">{receipt.signatureName}</p>
          <p className="text-[10px] muted">صدر آلياً — {formatDateTime(receipt.issuedAt)}</p>
        </div>
      </div>
    </div>
  );
});

/** A saved receipt with its print button. */
export function ReceiptPreview({ receipt }) {
  const sheet = useRef(null);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge className={(STATUS[receipt.status] || STATUS.issued).className}>{(STATUS[receipt.status] || STATUS.issued).label}</Badge>
        <Button icon="printer" onClick={() => printArea(sheet.current)}>
          طباعة الإيصال
        </Button>
      </div>
      <ReceiptSheet ref={sheet} receipt={receipt} />
    </div>
  );
}

/**
 * Issue a receipt.
 *
 * With `requests` (Accounts sent their balances to the desk) the lines are
 * those balances and cannot be edited — the server prices them again anyway.
 * Without, the desk types the services and amounts.
 */
export function ReceiptDialog({ open, onClose, patient = null, requests = [], defaults = {}, onIssued }) {
  const { success, error: toastError } = useNotifications();
  const settling = requests.length > 0;

  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [patientName, setPatientName] = useState('');
  const [affiliation, setAffiliation] = useState('');
  const [clinic, setClinic] = useState(defaults.clinic || '');
  const [doctor, setDoctor] = useState(defaults.doctor || '');
  const [lines, setLines] = useState([{ label: '', amount: '' }]);
  const [collected, setCollected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  const close = () => {
    setIssued(null);
    setLines([{ label: '', amount: '' }]);
    setCollected(false);
    onClose?.();
  };

  const fixedLines = requests.map((r) => ({
    label: `${DEPARTMENT_META[r.department]?.label || r.department}: ${r.title}`,
    amount: Number(r.amountDue || 0),
  }));
  const shownLines = settling ? fixedLines : lines;
  const total = shownLines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);

  const issue = async () => {
    setBusy(true);
    try {
      const receipt = await receiptsApi.issue({
        hospitalPatientId: patient?.id || null,
        patientName: patient ? undefined : patientName,
        title: title.trim() || DEFAULT_TITLE,
        affiliation: affiliation.trim() || undefined,
        clinic: clinic.trim() || undefined,
        doctor: doctor.trim() || undefined,
        items: settling
          ? fixedLines
          : lines.filter((l) => l.label.trim()).map((l) => ({ label: l.label.trim(), amount: Number(l.amount) || 0 })),
        serviceRequestIds: settling ? requests.map((r) => r.id) : undefined,
        collected: settling || collected,
      });
      success(`صدر الإيصال ${receipt.number}`);
      setIssued(receipt);
      onIssued?.(receipt);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={issued ? `الإيصال ${issued.number}` : 'إصدار إيصال إلكتروني'}
      icon="receipt"
      size="lg"
      footer={
        issued ? (
          <div className="flex justify-end">
            <Button variant="ghost" onClick={close}>
              تم
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-bold text-white">الإجمالي: {formatMoney(total)}</span>
            <Button icon="receipt" loading={busy} onClick={issue}>
              {settling ? 'تحصيل وإصدار الإيصال' : 'إصدار الإيصال'}
            </Button>
          </div>
        )
      }
    >
      {issued ? (
        <ReceiptPreview receipt={issued} />
      ) : (
        <div className="space-y-4">
          <p className="rounded-xl bg-cyan-500/10 px-3 py-2 text-xs text-cyan-200">
            <Icon name="info" size={13} className="inline" /> رقم الإيصال والتاريخ والوقت والتوقيع تُضاف تلقائياً عند الإصدار.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="عنوان الإيصال">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            {patient ? (
              <Field label="المريض">
                <Input value={patient.fullName} disabled />
              </Field>
            ) : (
              <Field label="اسم المريض" required>
                <Input value={patientName} onChange={(e) => setPatientName(e.target.value)} />
              </Field>
            )}
            <Field label="جهة الانتساب" hint="تُملأ من ملف المريض إن تُركت فارغة">
              <Input value={affiliation} onChange={(e) => setAffiliation(e.target.value)} />
            </Field>
            <Field label="العيادة">
              <Input value={clinic} onChange={(e) => setClinic(e.target.value)} />
            </Field>
            <Field label="الطبيب" className="sm:col-span-2">
              <Input value={doctor} onChange={(e) => setDoctor(e.target.value)} />
            </Field>
          </div>

          <div>
            <p className="mb-2 text-xs font-bold text-slate-400">الخدمات</p>
            {settling ? (
              <ul className="divide-y divide-white/5 rounded-xl border border-white/5">
                {fixedLines.map((l, i) => (
                  <li key={i} className="flex justify-between gap-3 p-2 text-sm text-slate-200">
                    <span>{l.label}</span>
                    <span className="font-bold">{formatMoney(l.amount)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="space-y-2">
                {lines.map((line, i) => (
                  <div key={i} className="grid grid-cols-[1fr_8rem_auto] gap-2">
                    <Input
                      value={line.label}
                      onChange={(e) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, label: e.target.value } : l)))}
                      placeholder="الخدمة"
                      aria-label="الخدمة"
                    />
                    <Input
                      type="number"
                      min="0"
                      value={line.amount}
                      onChange={(e) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, amount: e.target.value } : l)))}
                      placeholder="المبلغ"
                      aria-label="المبلغ"
                    />
                    <Button
                      variant="subtle"
                      icon="trash-2"
                      aria-label="حذف"
                      disabled={lines.length === 1}
                      onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                    />
                  </div>
                ))}
                <Button variant="subtle" icon="plus" onClick={() => setLines((ls) => [...ls, { label: '', amount: '' }])}>
                  خدمة أخرى
                </Button>
                <label className="mt-2 flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={collected} onChange={(e) => setCollected(e.target.checked)} />
                  تم تحصيل المبلغ الآن (وإلا يبقى آجلاً)
                </label>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/** A receipt fetched by id — for reprinting from a list. */
export function ReceiptById({ id }) {
  const [state, setState] = useState({ receipt: null, error: null });
  const load = () => {
    setState({ receipt: null, error: null });
    receiptsApi
      .get(id)
      .then((receipt) => setState({ receipt, error: null }))
      .catch((error) => setState({ receipt: null, error }));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (state.error) return <ErrorState error={state.error} onRetry={load} />;
  if (!state.receipt) return <Spinner label="جارٍ تحميل الإيصال" variant="inline" />;
  return <ReceiptPreview receipt={state.receipt} />;
}

export default ReceiptDialog;
